import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Alert,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Description as FileTextIcon,
  HelpOutline as HelpCircleIcon,
  FileUpload as FileUpIcon,
  InsertDriveFile as FileIcon,
  Error as CircleAlertIcon,
  Warning as TriangleAlertIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../App';
import { clearCachesAfterUpload, downloadRenewalNoticeTemplate, downloadRenewalNoticeErrorLog } from '../services/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { PageLayout } from '@/components/ui/PageLayout';
import { formatErrorForDisplay } from '@/utils/errorUtils';

// Chola Insurance theme colors (kept for consistency in messages)
const CHOLA_COLORS = {
  primary: '#003087',
  secondary: '#E31837',
  accent: '#FFB81C',
};

// Color scheme for file types
const FILE_TYPE_COLORS: Record<string, string> = {
  'hierarchy': '#003087',      // Primary blue
  'lead': '#16a34a',            // Green
  'lead-status': '#3b82f6',    // Blue
  'renewals': '#8b5cf6',       // Purple
  'products': '#6366f1',       // Indigo
  'renewal-notice': '#f59e0b', // Amber/Orange (distinctive)
};

// API Base URL
const API_BASE_URL = 'https://neokartuatbak.cholainsurance.com/api';

// File upload API endpoint mapping
const UPLOAD_ENDPOINTS: Record<string, string> = {
  'lead': '/uploads/lead-data',
  'hierarchy': '/uploads/hierarchy-data',
  'lead-status': '/uploads/lead-status',
  'renewals': '/uploads/renewal-targets',
  'products': '/uploads/products',
  // Renewal notice data upload (new)
  'renewal-notice': '/uploads/renewal-notice-data',
  // Propensity predictions upload
  'propensity': '/propensity/propensity-predictions'
};

// Status check API endpoint mapping
const STATUS_ENDPOINTS: Record<string, string> = {
  'lead': '/uploads/lead-data/status',
  'hierarchy': '/uploads/hierarchy-data/status',
  'lead-status': '/uploads/lead-status/status',
  'renewals': '/uploads/renewal-targets/status',
  'products': '/uploads/products/status',
  // Renewal notice data status (new)
  'renewal-notice': '/uploads/renewal-notice-data/status',
  // Propensity predictions status
  'propensity': '/propensity/propensity-predictions/status'
};

// API endpoints for filters
const FILTERS_ENDPOINT = '/dashboard/filters';

// Status types for task processing
export enum UploadTaskStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  WARNING = 'warning'
}

interface UploadTask {
  task_id: string;
  status: UploadTaskStatus;
  message: string;
  progress?: number;
  file_name?: string;
  created_at?: string;
  completed_at?: string;
  error_details?: string;
  error_messages?: string[];  // Array of error messages from API
  warnings?: string[];
  successful_rows?: number;
  failed_rows?: number;
}

interface FileTypeOption {
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode | null;
  adminOnly: boolean;
}

// API function to upload a file
const uploadFile = async (file: File, fileType: string, truncate: boolean = false, month?: string, year?: string, channel?: string): Promise<{ task_id: string; message: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const endpoint = UPLOAD_ENDPOINTS[fileType];
  if (!endpoint) {
    throw new Error(`Unknown file type: ${fileType}`);
  }

  // Build URL with query parameters for supported endpoints
  let url = `${API_BASE_URL}${endpoint}`;
  
  // Add query parameters based on file type
  const params = new URLSearchParams();
  
  if (['lead', 'hierarchy', 'renewals', 'renewal-notice'].includes(fileType)) {
    params.append('truncate', truncate.toString());
  }
  
  // Add month and year parameters for lead data when truncate is true
  if (fileType === 'lead' && truncate) {
    if (month) params.append('month', month);
    if (year) params.append('year', year);
  }
  
  // Add channel parameter for hierarchy data when truncate is true
  if (fileType === 'hierarchy' && truncate && channel) {
    params.append('channel', channel);
  }
  
  // Append params to URL if any exist
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  try {
    const token = localStorage.getItem('kc_token');
    const response = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    // Clear all caches after successful data upload
    // This ensures that the dashboard will fetch fresh data reflecting the upload
    clearCachesAfterUpload();
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.detail || 'Failed to upload file');
    }
    throw new Error('Network error occurred');
  }
};

// API function to check task status
const checkTaskStatus = async (taskId: string, fileType: string): Promise<UploadTask> => {
  const endpoint = STATUS_ENDPOINTS[fileType];
  if (!endpoint) {
    throw new Error(`Unknown file type: ${fileType}`);
  }

  try {
    const token = localStorage.getItem('kc_token');
    const response = await axios.get(`${API_BASE_URL}${endpoint}/${taskId}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    // Clear all caches if the task is completed
    // This ensures the dashboard will fetch fresh data
    if (response.data.status === UploadTaskStatus.COMPLETED) {
      clearCachesAfterUpload();
    }
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.detail || 'Failed to check task status');
    }
    throw new Error('Network error occurred');
  }
};

// API function to fetch filter options
const fetchFilterOptions = async (): Promise<{
  regions: string[];
  channel: string[];
  subchannel: string[];
  lob: string[];
  product_group: string[];
  sub_product: string[];
  months: { id: number; name: string; }[];
  years: { id: string; name: string; start_year: number; end_year: number; }[];
}> => {
  try {
    const token = localStorage.getItem('kc_token');
    const response = await axios.get(`${API_BASE_URL}${FILTERS_ENDPOINT}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to fetch filter options:', error);
    // Return default empty structure
    return {
      regions: [],
      channel: [],
      subchannel: [],
      lob: [],
      product_group: [],
      sub_product: [],
      months: [],
      years: []
    };
  }
};

export function ExcelUpload() {
  const [fileType, setFileType] = useState('hierarchy');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentTask, setCurrentTask] = useState<UploadTask | null>(null);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const [truncateData, setTruncateData] = useState(false);
  const [truncateConfirmed, setTruncateConfirmed] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [downloadingErrorLog, setDownloadingErrorLog] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState<{
    regions: string[];
    channel: string[];
    subchannel: string[];
    lob: string[];
    product_group: string[];
    sub_product: string[];
    months: { id: number; name: string; }[];
    years: { id: string; name: string; start_year: number; end_year: number; }[];
  }>({
    regions: [],
    channel: [],
    subchannel: [],
    lob: [],
    product_group: [],
    sub_product: [],
    months: [],
    years: []
  });
  
  const pollingTimeoutRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Get auth context to check user roles
  const { token } = useContext(AuthContext);
  
  // Check if user has Admin role
  const hasAdminRole = React.useMemo(() => {
    if (!token) return false;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const parsedToken = JSON.parse(jsonPayload);
      return parsedToken.realm_access?.roles?.includes('Admin') || false;
    } catch (error) {
      console.error('Error checking admin role:', error);
      return false;
    }
  }, [token]);
  
  // Define all file type options
  const allFileTypeOptions: FileTypeOption[] = [
    {
      value: 'hierarchy',
      label: 'Hierarchy Data',
      description: 'Upload organizational hierarchy structure',
      icon: null, // Icon removed - using File icon in UI instead
      adminOnly: true
    },
    {
      value: 'lead',
      label: 'Lead Data',
      description: 'Upload customer lead information',
      icon: null,
      adminOnly: true
    },
    {
      value: 'lead-status',
      label: 'Lead Status Data',
      description: 'Upload lead status details and information',
      icon: null,
      adminOnly: false
    },
    {
      value: 'renewals',
      label: 'Renewal Targets',
      description: 'Upload renewal target information',
      icon: null,
      adminOnly: true
    },
    {
      value: 'products',
      label: 'Products Data',
      description: 'Upload product catalog and information',
      icon: null,
      adminOnly: true
    },
    //Do not remove this, it is used for propensity scoring
    // {
    //   value: 'propensity',
    //   label: 'Propensity Predictions',
    //   description: 'Upload predictions CSV and track async processing',
    //   icon: null,
    //   adminOnly: true
    // },
    {
      value: 'renewal-notice',
      label: 'Renewal Notice Data',
      description: 'Upload renewal notice data (policy, journey link, etc.)',
      icon: null,
      adminOnly: true
    }
  ];
  
  // Filter file type options based on admin role
  const fileTypeOptions = allFileTypeOptions.filter(option => 
    !option.adminOnly || hasAdminRole
  );
  
  // Update fileType if current selection is no longer available
  useEffect(() => {
    if (!fileTypeOptions.some(option => option.value === fileType) && fileTypeOptions.length > 0) {
      setFileType(fileTypeOptions[0].value);
    }
  }, [fileTypeOptions, fileType]);
  
  // Clear any active polling when changing file types
  useEffect(() => {
    // Clear any active polling when changing file types
    if (pollingTimeoutRef.current) {
      window.clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    
    setCurrentTask(null);
  }, [fileType]);

  // Load filter options when component mounts
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await fetchFilterOptions();
        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    
    loadFilterOptions();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        window.clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const startPolling = (taskId: string) => {
    // Define a polling function
    const pollStatus = async () => {
      try {
        const taskStatus = await checkTaskStatus(taskId, fileType);
        setCurrentTask(taskStatus);
        
        // If the task is completed or failed, stop polling
        if (
          taskStatus.status === UploadTaskStatus.COMPLETED || 
          taskStatus.status === UploadTaskStatus.FAILED
        ) {
          if (taskStatus.status === UploadTaskStatus.COMPLETED) {
            setSuccess(true);
            setSuccessMessage(taskStatus.message || 'File processed successfully');
            // No snackbar; success UI shown inline
          } else if (taskStatus.status === UploadTaskStatus.FAILED) {
            setError(taskStatus.message || 'File processing failed');
          }
          
          setLoading(false);
          
          // Clear polling timeout
          if (pollingTimeoutRef.current) {
            window.clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
        } else {
          // Continue polling if the task is not yet complete
          pollingTimeoutRef.current = window.setTimeout(pollStatus, 2000);
        }
      } catch (err) {
        console.error('Error polling task status:', err);
        setError('Failed to check task status. Please refresh to see the latest status.');
        setLoading(false);
        
        // Clear polling timeout on error
        if (pollingTimeoutRef.current) {
          window.clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
      }
    };

    // Start polling immediately
    pollStatus();
  };

  const handleFileTypeChange = (newFileType: string) => {
    setFileType(newFileType);
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
    setCurrentTask(null);
    setTruncateData(false);
    setTruncateConfirmed(false);
    setSelectedMonth('');
    setSelectedYear('');
    setSelectedChannel('');
    setDownloadingErrorLog(false);
    setShowErrorDetails(false);
  };

  const handleDownloadErrorLog = async () => {
    console.log('handleDownloadErrorLog called', { currentTask, fileType, task_id: currentTask?.task_id });
    
    if (!currentTask?.task_id) {
      console.warn('No current task or task_id available');
      const errorMsg = 'No upload task available. Please upload a file first.';
      setError(errorMsg);
      alert(errorMsg); // Show alert as immediate feedback
      return;
    }
    
    // Only allow download for renewal-notice file type
    if (fileType !== 'renewal-notice') {
      console.warn('Error log download only available for renewal-notice, current fileType:', fileType);
      const errorMsg = 'Error log download is only available for Renewal Notice Data uploads';
      setError(errorMsg);
      alert(errorMsg); // Show alert as immediate feedback
      return;
    }
    
    console.log('Starting download for task:', currentTask.task_id);
    setDownloadingErrorLog(true);
    try {
      await downloadRenewalNoticeErrorLog(currentTask.task_id);
      console.log('Download completed successfully');
      // Success - the download should have triggered automatically
    } catch (err: any) {
      console.error('Error downloading error log:', err);
      let errorMsg = 'Failed to download error log';
      
      // Handle timeout errors specifically
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        errorMsg = 'Download request timed out. Please try again or contact support if the issue persists.';
      } else if (err instanceof Error) {
        errorMsg = err.message;
      } else if (err?.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      
      setError(errorMsg);
      alert(`Error: ${errorMsg}`); // Show alert for errors
    } finally {
      // Always reset loading state, even if there's an error
      setDownloadingErrorLog(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadRenewalNoticeTemplate();
    } catch (err) {
      console.error('Error downloading template:', err);
      setError(err instanceof Error ? err.message : 'Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // For propensity, only allow CSV
      const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel' || file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
      const allowed = fileType === 'propensity' ? isCsv : (isExcel || isCsv);
      if (allowed) {
        setSelectedFile(file);
        setError(null);
        setSuccess(false);
        setCurrentTask(null);
        // Reset truncate checkboxes when file changes
        setTruncateData(false);
        setTruncateConfirmed(false);
      } else {
        setError(fileType === 'propensity'
          ? 'Please upload only CSV files (.csv) for Propensity Predictions'
          : 'Please upload only Excel files (.xlsx or .xls) or CSV (.csv)');
        setSelectedFile(null);
        setSuccess(false);
        setCurrentTask(null);
      }
    }
    
    // Reset the input value so that selecting the same file again will trigger onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    // Validate month and year when truncate is enabled for lead data
    if (fileType === 'lead' && truncateData) {
      if (selectedMonth && !selectedMonth.match(/^([1-9]|1[0-2])$/)) {
        setError('Please enter a valid month (1-12)');
        return;
      }
      
      if (selectedYear && !selectedYear.match(/^\d{4}$/)) {
        setError('Please enter a valid 4-digit year');
        return;
      }
    }

    // Show confirmation dialog if truncate is enabled
    if (truncateData) {
      setConfirmDialogOpen(true);
      return;
    }

    // Proceed with upload if no truncation
    performUpload();
  };

  const performUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setCurrentTask(null);
    setShowErrorDetails(false);
    setConfirmDialogOpen(false);
    
    try {
      // Pass parameters based on file type
      const result = await uploadFile(
        selectedFile, 
        fileType, 
        truncateData,
        fileType === 'lead' && truncateData ? selectedMonth : undefined,
        fileType === 'lead' && truncateData ? selectedYear : undefined,
        fileType === 'hierarchy' && truncateData ? selectedChannel : undefined
      );
      
      // Set initial task information
      setCurrentTask({
        task_id: result.task_id,
        status: UploadTaskStatus.PENDING,
        message: result.message || 'Upload initialized',
        progress: 0
      });
      
      // Start polling for status updates
      startPolling(result.task_id);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setLoading(false);
    }
  };

  // Snackbar removed; success feedback shown inline

  const getSelectedFileType = () => {
    const option = fileTypeOptions.find(option => option.value === fileType);
    // Return first option or a fallback if none found
    return option || (fileTypeOptions.length > 0 ? fileTypeOptions[0] : {
      value: '',
      label: 'No options available',
      description: '',
      icon: null,
      adminOnly: false
    });
  };

  const getFileTypeColor = (type: string) => {
    return FILE_TYPE_COLORS[type] || FILE_TYPE_COLORS['hierarchy'];
  };

  const getTruncationWarningMessage = () => {
    switch (fileType) {
      case 'hierarchy':
        return 'This will replace all existing hierarchy data. This action cannot be undone.';
      case 'lead':
        return 'This will replace existing lead data' + (selectedMonth || selectedYear ? ` for the selected ${selectedMonth ? 'month' : ''}${selectedMonth && selectedYear ? ' and ' : ''}${selectedYear ? 'year' : ''}` : '') + '. This action cannot be undone.';
      case 'renewal-notice':
        return 'This will replace existing renewal notice data by policy number. This action cannot be undone.';
      case 'renewals':
        return 'This will replace all existing renewal targets. This action cannot be undone.';
      default:
        return 'This will replace existing data. This action cannot be undone.';
    }
  };

  const getStatusIcon = (status: UploadTaskStatus) => {
    switch (status) {
      case UploadTaskStatus.COMPLETED:
        return <CheckCircleIcon sx={{ width: '16px', height: '16px', color: '#16a34a' }} />;
      case UploadTaskStatus.FAILED:
        return <CircleAlertIcon sx={{ width: '16px', height: '16px', color: '#dc2626' }} />;
      case UploadTaskStatus.PROCESSING:
      case UploadTaskStatus.PENDING:
        return <CircularProgress size={16} sx={{ color: '#003087' }} />;
      case UploadTaskStatus.WARNING:
        return <TriangleAlertIcon sx={{ width: '16px', height: '16px', color: '#f59e0b' }} />;
      default:
        return <HelpCircleIcon sx={{ width: '16px', height: '16px' }} />;
    }
  };

  // Get list of years (current year and 5 years back)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentYear - i);
  };

  // Get list of months
  const getMonthOptions = () => {
    return [
      { value: '1', label: 'January' },
      { value: '2', label: 'February' },
      { value: '3', label: 'March' },
      { value: '4', label: 'April' },
      { value: '5', label: 'May' },
      { value: '6', label: 'June' },
      { value: '7', label: 'July' },
      { value: '8', label: 'August' },
      { value: '9', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' },
    ];
  };

  return (
    <PageLayout>
      <PageHeader
        icon={<FileUpIcon />}
        title="Data Upload Center"
        description="Upload and process Excel files to update system data"
      />

      {/* Main Layout - Using CSS Grid */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',                    // Mobile: single column
          md: '280px 1fr',              // Tablet: 2 columns
          lg: '280px 1fr 300px'         // Desktop: 3 columns
        },
        gap: 3,
        gridTemplateRows: 'auto',
        minHeight: 'calc(100vh - 200px)'
      }}>
        {/* Left Sidebar - File Type Selection */}
        <Box sx={{ gridColumn: { xs: '1', md: '1', lg: '1' } }}>
          <Card sx={{ height: '100%', border: `1px solid ${getFileTypeColor(fileType)}33` }}>
            <CardHeader sx={(theme) => ({ 
              paddingBottom: '8px', 
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(249, 250, 251, 0.5)', 
              borderBottom: `1px solid ${theme.palette.divider}` 
            })}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <FileTextIcon sx={{ width: '18px', height: '18px', color: getFileTypeColor(fileType) }} />
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Select File Type
                </Typography>
              </Box>
            </CardHeader>
            <CardContent sx={{ padding: '16px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {fileTypeOptions.map((option) => {
                  const isSelected = fileType === option.value;
                  const optionColor = getFileTypeColor(option.value);
                  return (
                    <Button
                      key={option.value}
                      onClick={() => handleFileTypeChange(option.value)}
                      variant={isSelected ? 'contained' : 'outlined'}
                      sx={{
                        width: '100%',
                        borderRadius: 1,
                        border: isSelected ? `2px solid ${optionColor}` : `1px solid rgba(0, 0, 0, 0.12)`,
                        backgroundColor: isSelected ? `${optionColor}15` : 'transparent',
                        padding: '10px',
                        textAlign: 'left',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        boxShadow: isSelected ? `0 2px 4px ${optionColor}33` : 'none',
                        '&:hover': {
                          backgroundColor: isSelected ? `${optionColor}25` : 'rgba(0, 0, 0, 0.04)',
                          borderColor: isSelected ? optionColor : 'rgba(0, 0, 0, 0.23)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                        <Box sx={{ 
                          width: '4px', 
                          height: '100%', 
                          backgroundColor: isSelected ? optionColor : 'transparent',
                          borderRadius: '2px',
                          flexShrink: 0
                        }} />
                        <FileIcon sx={{
                          width: '18px',
                          height: '18px',
                          marginTop: '2px',
                          flexShrink: 0,
                          color: isSelected ? optionColor : 'rgba(0, 0, 0, 0.54)'
                        }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{
                            fontSize: '14px',
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? optionColor : 'rgba(0, 0, 0, 0.87)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {option.label}
                          </Typography>
                          <Typography sx={{
                            fontSize: '12px',
                            color: 'rgba(0, 0, 0, 0.6)',
                            marginTop: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right Content - Upload Area */}
        <Box sx={{ gridColumn: { xs: '1', md: '2', lg: '2' } }}>
            <Card sx={{ 
              height: '100%',
              border: `1px solid ${getFileTypeColor(fileType)}33`,
              borderLeft: { md: `4px solid ${getFileTypeColor(fileType)}` }
            }}>
              <CardHeader sx={(theme) => ({ 
                paddingBottom: '8px', 
                backgroundColor: `${getFileTypeColor(fileType)}08`,
                borderBottom: `2px solid ${getFileTypeColor(fileType)}33`
              })}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={getSelectedFileType().label}
                        sx={{ 
                          backgroundColor: `${getFileTypeColor(fileType)}15`,
                          color: getFileTypeColor(fileType),
                          fontWeight: 600,
                          border: `1px solid ${getFileTypeColor(fileType)}40`
                        }}
                        size="small"
                      />
                      <CloudUploadIcon sx={{ width: '18px', height: '18px', color: getFileTypeColor(fileType) }} />
                      <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: getFileTypeColor(fileType) }}>
                        Upload {getSelectedFileType().label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', marginTop: '8px', color: 'text.secondary' }}>
                      {getSelectedFileType().description}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '11px', marginTop: '4px', color: 'text.secondary', display: 'block' }}>
                      {fileType === 'propensity' ? 'Supported format: .csv' : 'Supported formats: .xlsx, .xls, .csv'}
                    </Typography>
                  </Box>
                  <Tooltip title="Select a file type from the left panel to upload data">
                    <HelpCircleIcon sx={{ width: '20px', height: '20px', color: 'rgba(0, 0, 0, 0.54)', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </CardHeader>

              <CardContent sx={{ padding: '16px' }}>
                {success ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, padding: '32px 0', textAlign: 'center' }}>
                    <CheckCircleIcon sx={(theme) => ({ width: '64px', height: '64px', color: theme.palette.success.main })} />
                    <Typography variant="h6" sx={(theme) => ({ fontSize: '18px', fontWeight: 600, color: theme.palette.success.dark })}>
                      {successMessage}
                    </Typography>
                    {/* Display success and failure counts */}
                    {fileType === 'renewal-notice' && currentTask && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Chip 
                          label={`${currentTask.successful_rows || 0} Successful`} 
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip 
                          label={`${currentTask.failed_rows || 0} Failed`} 
                          color={currentTask.failed_rows && currentTask.failed_rows > 0 ? "error" : "default"}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    )}
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedFile(null);
                        setSuccess(false);
                        setCurrentTask(null);
                      }}
                    >
                      Upload Another File
                    </Button>
                  </Box>
                ) : (
                  <>
                    {/* Download Template Button - Only for renewal-notice */}
                    {fileType === 'renewal-notice' && (
                      <Box sx={{ marginBottom: 2 }}>
                        <Button
                          variant="outlined"
                          size="large"
                          onClick={handleDownloadTemplate}
                          disabled={downloadingTemplate}
                          fullWidth
                          sx={(theme) => ({
                            borderColor: theme.palette.success.main,
                            color: theme.palette.success.main,
                            backgroundColor: downloadingTemplate ? theme.palette.action.hover : 'transparent',
                            '&:hover': {
                              borderColor: theme.palette.success.main,
                              backgroundColor: `${theme.palette.success.main}0A`,
                            }
                          })}
                          startIcon={downloadingTemplate ? <CircularProgress size={20} /> : <FileTextIcon />}
                        >
                          {downloadingTemplate ? 'Downloading Template...' : 'Download Excel Template'}
                        </Button>
                        {/* <Typography variant="body2" sx={{ marginTop: 1, fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                          Download a pre-formatted template with all required columns and sample data
                        </Typography> */}
                      </Box>
                    )}

                    {/* Upload Zone */}
                    <Box sx={(theme) => ({
                      borderRadius: 1,
                      border: `2px dashed ${theme.palette.divider}`,
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : 'rgba(249, 250, 251, 0.7)',
                      padding: 3,
                      textAlign: 'center',
                      transition: 'border-color 0.2s'
                    })}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          accept={fileType === 'propensity' ? '.csv' : '.xlsx,.xls,.csv'}
                          onChange={handleFileChange}
                        />
                        <Button
                          size="large"
                          variant={selectedFile ? 'outlined' : 'contained'}
                          disabled={loading}
                          onClick={() => fileInputRef.current?.click()}
                          startIcon={<CloudUploadIcon />}
                          sx={(theme) => (!selectedFile ? { backgroundColor: theme.palette.primary.main } : {})}
                        >
                          {selectedFile ? 'Change File' : `Select ${getSelectedFileType().label} File`}
                        </Button>
                        {selectedFile ? (
                          <Box sx={(theme) => ({ display: 'flex', alignItems: 'center', gap: 1, padding: '8px 16px', borderRadius: '6px', backgroundColor: `${theme.palette.primary.main}1A` })}>
                            <FileIcon sx={(theme) => ({ width: '16px', height: '16px', color: theme.palette.primary.main })} />
                            <Typography sx={(theme) => ({ fontWeight: 500, color: theme.palette.text.primary })}>{selectedFile.name}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={(theme) => ({ fontSize: '14px', color: theme.palette.text.secondary, margin: 0 })}>
                            Drag and drop your Excel file here, or click to browse
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Task Status */}
                    {currentTask && (
                      <Box sx={(theme) => ({ 
                        marginTop: 2, 
                        padding: 2, 
                        borderRadius: 1.5, 
                        border: currentTask.status === UploadTaskStatus.FAILED 
                          ? `1px solid ${theme.palette.error.light}` 
                          : `1px solid ${theme.palette.divider}`,
                        backgroundColor: currentTask.status === UploadTaskStatus.FAILED 
                          ? `${theme.palette.error.main}14` 
                          : (theme.palette.mode === 'dark' ? theme.palette.background.default : 'rgba(249, 250, 251, 0.7)'),
                        boxShadow: currentTask.status === UploadTaskStatus.FAILED 
                          ? `0 1px 3px 0 ${theme.palette.error.main}1A` 
                          : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                      })}>
                        <Box sx={{ 
                          marginBottom: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          paddingBottom: 1,
                          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(currentTask.status)}
                            <Typography sx={(theme) => ({ 
                              fontSize: '15px', 
                              fontWeight: 600, 
                              color: currentTask.status === UploadTaskStatus.FAILED ? theme.palette.error.main : theme.palette.text.primary
                            })}>
                              {currentTask.status === UploadTaskStatus.FAILED ? 'Upload Failed' : `Processing Status: ${currentTask.status.charAt(0).toUpperCase() + currentTask.status.slice(1)}`}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {currentTask.status === UploadTaskStatus.FAILED ? (
                          <Box>
                            <Typography sx={(theme) => ({ 
                              marginBottom: 2, 
                              fontSize: '14px', 
                              color: theme.palette.text.secondary,
                              lineHeight: '1.5'
                            })}>
                              {currentTask.error_messages && currentTask.error_messages.length > 0
                                ? `Found ${currentTask.error_messages.length} ${currentTask.error_messages.length === 1 ? 'error' : 'errors'} during processing. Please review the details below.`
                                : currentTask.message || 'File processing failed'}
                            </Typography>
                            
                            {/* Show error messages if available */}
                            {(currentTask.error_messages && currentTask.error_messages.length > 0) && (
                              <Box sx={{ marginBottom: 2 }}>
                                <Button
                                  variant={showErrorDetails ? "contained" : "outlined"}
                                  size="small"
                                  onClick={() => setShowErrorDetails(!showErrorDetails)}
                                  fullWidth
                                  startIcon={<CircleAlertIcon />}
                                  sx={(theme) => ({ 
                                    marginBottom: showErrorDetails ? 1.5 : 0,
                                    backgroundColor: showErrorDetails ? theme.palette.action.hover : 'transparent',
                                    borderColor: theme.palette.divider,
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                  })}
                                >
                                  {showErrorDetails ? 'Hide' : 'View'} Error Details 
                                  <Box component="span" sx={(theme) => ({ 
                                    marginLeft: 1,
                                    padding: '2px 8px',
                                    backgroundColor: showErrorDetails ? theme.palette.background.paper : `${theme.palette.error.main}26`,
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: theme.palette.error.main
                                  })}>
                                    {currentTask.error_messages.length}
                                  </Box>
                                </Button>
                                {showErrorDetails && (
                                  <Box sx={(theme) => ({
                                    marginTop: 1.5,
                                    padding: 2,
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.error.light}`,
                                    borderRadius: 1,
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
                                  })}>
                                    <Typography sx={(theme) => ({
                                      marginBottom: 1.5,
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      color: theme.palette.error.dark
                                    })}>
                                      Error Details:
                                    </Typography>
                                    <Box component="ul" sx={(theme) => ({ 
                                      margin: 0, 
                                      paddingLeft: '20px', 
                                      fontSize: '13px', 
                                      color: theme.palette.error.dark,
                                      lineHeight: '1.8'
                                    })}>
                                      {currentTask.error_messages.map((errorMsg, index) => (
                                        <Box component="li" key={index} sx={{ 
                                          marginBottom: 1,
                                          paddingLeft: '4px'
                                        }}>
                                          {formatErrorForDisplay(errorMsg)}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}
                            
                            {/* Download Error Log button */}
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Download Error Log button clicked');
                                handleDownloadErrorLog();
                              }}
                              disabled={downloadingErrorLog || !currentTask?.task_id}
                              fullWidth
                              startIcon={<FileTextIcon />}
                              sx={(theme) => ({ 
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                textTransform: 'none',
                                marginTop: 2,
                              })}
                            >
                              {downloadingErrorLog ? 'Downloading...' : 'Download Error Log as CSV'}
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <Typography sx={(theme) => ({ marginBottom: 1.5, fontSize: '14px', color: theme.palette.text.secondary })}>
                              {currentTask.message}
                            </Typography>
                            {/* Display success and failure counts when completed */}
                            {fileType === 'renewal-notice' && currentTask.status === UploadTaskStatus.COMPLETED && (
                              <Box sx={{ display: 'flex', gap: 2, mt: 1.5, mb: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Chip 
                                  label={`${currentTask.successful_rows || 0} Successful`} 
                                  color="success"
                                  sx={{ fontWeight: 600 }}
                                />
                                <Chip 
                                  label={`${currentTask.failed_rows || 0} Failed`} 
                                  color={currentTask.failed_rows && currentTask.failed_rows > 0 ? "error" : "default"}
                                  sx={{ fontWeight: 600 }}
                                />
                              </Box>
                            )}
                            {(currentTask.status === UploadTaskStatus.PENDING || currentTask.status === UploadTaskStatus.PROCESSING) && (
                              <LinearProgress 
                                variant="determinate" 
                                value={currentTask.progress ?? 0} 
                                sx={{ height: '8px', marginTop: 1.5 }} 
                              />
                            )}
                            {currentTask.warnings && currentTask.warnings.length > 0 && (
                              <Alert severity="warning" sx={{ marginTop: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Warnings</Typography>
                                <Box component="ul" sx={{ margin: '8px 0', paddingLeft: '20px' }}>
                                  {currentTask.warnings.map((warning, index) => (
                                    <Box component="li" key={index}>{warning}</Box>
                                  ))}
                                </Box>
                              </Alert>
                            )}
                          </>
                        )}
                      </Box>
                    )}

                    {/* Error Alert - Only show for non-upload errors (e.g., network errors, validation before upload) */}
                    {/* Hide when task failed since we show detailed errors in the task status section */}
                    {error && !(currentTask && currentTask.status === UploadTaskStatus.FAILED) && (
                      <Alert severity="error" sx={{ marginTop: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Error</Typography>
                        <Typography variant="body2">{error}</Typography>
                      </Alert>
                    )}

                    {/* Pre-Upload Summary */}
                    {selectedFile && (
                      <Box sx={{ 
                        marginTop: 2, 
                        padding: 2, 
                        borderRadius: 1.5,
                        border: `1px solid ${getFileTypeColor(fileType)}40`,
                        backgroundColor: `${getFileTypeColor(fileType)}08`
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: 1.5, color: getFileTypeColor(fileType) }}>
                          Upload Summary
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileIcon sx={{ width: '16px', height: '16px', color: getFileTypeColor(fileType) }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {selectedFile.name}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                          {truncateData && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                ⚠️ Data Replacement Enabled
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                {getTruncationWarningMessage()}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Truncate Options */}
                    {['lead', 'hierarchy', 'renewals', 'renewal-notice'].includes(fileType) && (
                      <Box sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{
                          padding: 2,
                          borderRadius: 1.5,
                          border: truncateData ? `2px solid #f59e0b` : `1px solid rgba(0, 0, 0, 0.12)`,
                          backgroundColor: truncateData ? '#fef3c733' : 'transparent',
                          transition: 'all 0.2s'
                        }}>
                          <FormControlLabel
                            control={
                              <Checkbox 
                                checked={truncateData} 
                                onChange={(e) => {
                                  setTruncateData(e.target.checked);
                                  if (!e.target.checked) {
                                    setTruncateConfirmed(false);
                                  }
                                }}
                                sx={{
                                  color: truncateData ? '#f59e0b' : 'inherit',
                                  '&.Mui-checked': {
                                    color: '#f59e0b',
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography sx={{ fontWeight: truncateData ? 600 : 400, fontSize: '14px' }}>
                                {fileType === 'renewal-notice' ? "Replace existing data (by policy number)" : "Replace existing data (truncate)"}
                              </Typography>
                            }
                          />
                          {truncateData && (
                            <Box sx={{ mt: 1.5 }}>
                              <Alert severity="warning" icon={<TriangleAlertIcon />}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Warning: Destructive Action
                                </Typography>
                                <Typography variant="caption">
                                  {getTruncationWarningMessage()}
                                </Typography>
                              </Alert>
                              <FormControlLabel
                                control={
                                  <Checkbox 
                                    checked={truncateConfirmed} 
                                    onChange={(e) => setTruncateConfirmed(e.target.checked)}
                                    sx={{
                                      color: '#f59e0b',
                                      '&.Mui-checked': {
                                        color: '#f59e0b',
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <Typography variant="caption" sx={{ fontSize: '12px' }}>
                                    I understand this will replace existing data
                                  </Typography>
                                }
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          )}
                        </Box>

                        {fileType === 'lead' && truncateData && (
                          <Box sx={(theme) => ({ padding: 1.5, borderRadius: 1, border: `1px solid ${theme.palette.primary.main}33`, backgroundColor: `${theme.palette.primary.main}0D` })}>
                            <Typography sx={(theme) => ({ marginBottom: 1, fontSize: '14px', fontWeight: 500, color: theme.palette.primary.main })}>
                              Optional: Select specific month/year to delete data for
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Month (optional)</InputLabel>
                                <Select 
                                  value={selectedMonth} 
                                  label="Month (optional)"
                                  onChange={(e) => setSelectedMonth(e.target.value)}
                                >
                                  <MenuItem value="">All months</MenuItem>
                                  {getMonthOptions().map((m) => (
                                    <MenuItem key={m.value} value={m.value}>
                                      {m.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography variant="caption" sx={(theme) => ({ marginTop: '4px', fontSize: '12px', color: theme.palette.text.secondary, display: 'block' })}>
                                  Select to delete data for specific months (1-12)
                                </Typography>
                              </FormControl>
                              <FormControl fullWidth size="small">
                                <InputLabel>Year (optional)</InputLabel>
                                <Select 
                                  value={selectedYear} 
                                  label="Year (optional)"
                                  onChange={(e) => setSelectedYear(e.target.value)}
                                >
                                  <MenuItem value="">All years</MenuItem>
                                  {getYearOptions().map((year) => (
                                    <MenuItem key={year} value={year.toString()}>
                                      {year}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography variant="caption" sx={(theme) => ({ marginTop: '4px', fontSize: '12px', color: theme.palette.text.secondary, display: 'block' })}>
                                  Select to delete data for specific years
                                </Typography>
                              </FormControl>
                            </Box>
                          </Box>
                        )}

                        {fileType === 'hierarchy' && truncateData && (
                          <Box sx={(theme) => ({ padding: 1.5, borderRadius: 1, border: `1px solid ${theme.palette.primary.main}33`, backgroundColor: `${theme.palette.primary.main}0D` })}>
                            <Typography sx={(theme) => ({ marginBottom: 1, fontSize: '14px', fontWeight: 500, color: theme.palette.primary.main })}>
                              Optional: Select specific channel to delete data for
                            </Typography>
                            <FormControl fullWidth size="small">
                              <InputLabel>Channel (optional)</InputLabel>
                              <Select 
                                value={selectedChannel} 
                                label="Channel (optional)"
                                onChange={(e) => setSelectedChannel(e.target.value)}
                              >
                                <MenuItem value="">All channels</MenuItem>
                                {filterOptions.channel.map((channel) => (
                                  <MenuItem key={channel} value={channel}>
                                    {channel}
                                  </MenuItem>
                                ))}
                              </Select>
                              <Typography variant="caption" sx={(theme) => ({ marginTop: '4px', fontSize: '12px', color: theme.palette.text.secondary, display: 'block' })}>
                                Select to truncate data for a specific channel only
                              </Typography>
                            </FormControl>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Upload Button */}
                    <Button
                      size="large"
                      variant="contained"
                      fullWidth
                      sx={{ 
                        marginTop: 2, 
                        backgroundColor: getFileTypeColor(fileType),
                        '&:hover': {
                          backgroundColor: getFileTypeColor(fileType),
                          opacity: 0.9
                        },
                        '&:disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.12)'
                        }
                      }}
                      onClick={handleUpload}
                      disabled={!selectedFile || loading || (truncateData && !truncateConfirmed)}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {loading ? 'Uploading...' : 'Upload File'}
                    </Button>
                    {truncateData && !truncateConfirmed && (
                      <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                        Please confirm that you understand data will be replaced
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
        </Box>

        {/* Summary Panel - Grid Area 3 (desktop only) */}
        <Box sx={{ 
          gridColumn: { xs: '1', md: '2', lg: '3' },
          display: { xs: 'none', lg: 'block' }
        }}>
          <Card sx={{ 
            height: '100%',
            border: `1px solid ${getFileTypeColor(fileType)}33`,
            position: 'sticky',
            top: 24
          }}>
            <CardHeader sx={{ 
              paddingBottom: '8px', 
              backgroundColor: `${getFileTypeColor(fileType)}08`,
              borderBottom: `1px solid ${getFileTypeColor(fileType)}33`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <FileTextIcon sx={{ width: '18px', height: '18px', color: getFileTypeColor(fileType) }} />
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Upload Summary
                </Typography>
              </Box>
            </CardHeader>
            <CardContent sx={{ padding: '16px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* File Type */}
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                    File Type
                  </Typography>
                  <Chip 
                    label={getSelectedFileType().label}
                    sx={{ 
                      mt: 0.5,
                      backgroundColor: `${getFileTypeColor(fileType)}15`,
                      color: getFileTypeColor(fileType),
                      fontWeight: 600,
                      border: `1px solid ${getFileTypeColor(fileType)}40`
                    }}
                    size="small"
                  />
                </Box>

                {/* Selected File */}
                {selectedFile ? (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      Selected File
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileIcon sx={{ width: '16px', height: '16px', color: getFileTypeColor(fileType) }} />
                      <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {selectedFile.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', display: 'block', mt: 0.5 }}>
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      Selected File
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
                      No file selected
                    </Typography>
                  </Box>
                )}

                {/* Truncation Status */}
                {['lead', 'hierarchy', 'renewals', 'renewal-notice'].includes(fileType) && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      Data Replacement
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {truncateData ? (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Enabled
                          </Typography>
                        </Alert>
                      ) : (
                        <Chip label="Disabled" size="small" color="default" />
                      )}
                    </Box>
                  </Box>
                )}

                {/* Additional Options */}
                {(selectedMonth || selectedYear || selectedChannel) && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      Additional Options
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {selectedMonth && (
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>
                          Month: {getMonthOptions().find(m => m.value === selectedMonth)?.label}
                        </Typography>
                      )}
                      {selectedYear && (
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>
                          Year: {selectedYear}
                        </Typography>
                      )}
                      {selectedChannel && (
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>
                          Channel: {selectedChannel}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Upload Status */}
                {currentTask && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      Upload Status
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(currentTask.status)}
                      <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500 }}>
                        {currentTask.status.charAt(0).toUpperCase() + currentTask.status.slice(1)}
                      </Typography>
                    </Box>
                    {currentTask.progress !== undefined && (
                      <LinearProgress 
                        variant="determinate" 
                        value={currentTask.progress} 
                        sx={{ height: '4px', mt: 1 }} 
                      />
                    )}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TriangleAlertIcon sx={{ color: '#f59e0b' }} />
            <Typography variant="h6">
              Confirm Data Upload
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Data Replacement Warning
              </Typography>
              <Typography variant="body2">
                {getTruncationWarningMessage()}
              </Typography>
            </Alert>

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Details:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={getSelectedFileType().label}
                    sx={{ 
                      backgroundColor: `${getFileTypeColor(fileType)}15`,
                      color: getFileTypeColor(fileType),
                      fontWeight: 600,
                      border: `1px solid ${getFileTypeColor(fileType)}40`
                    }}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FileIcon sx={{ width: '16px', height: '16px' }} />
                  {selectedFile?.name}
                </Typography>
                {selectedFile && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    File size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                )}
              </Box>
            </Box>

            <DialogContentText>
              Are you sure you want to proceed with this upload? This action cannot be undone.
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={performUpload}
            variant="contained"
            sx={{ 
              backgroundColor: getFileTypeColor(fileType),
              '&:hover': {
                backgroundColor: getFileTypeColor(fileType),
                opacity: 0.9
              }
            }}
            startIcon={<CloudUploadIcon />}
          >
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
